import React, { useState } from 'react';
import { useLocation, Navigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, Sparkles, CheckCircle2, ShieldCheck, Stethoscope,
  TrendingDown, ChevronDown, MessageCircle, Plane, Award, FileCheck2,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { useCatalog } from '../context/CatalogContext';
import { buildLocalizedPath } from '../components/LocaleRouter';
import {
  getCategoryContentByUrl,
  ALL_CATEGORY_LANDINGS,
} from '../data/categoryLandingContent';
import { useContactInfo, getWhatsAppHref } from '../contexts/ContactInfoContext';
import { buildBreadcrumbJsonLd } from '../lib/seo';

/**
 * Reusable SEO landing page for a treatment category.
 *
 * Reads the URL pathname (locale-aware), looks up the matching entry in
 * `src/data/categoryLandingContent.js`, and auto-pulls every treatment in
 * that category from the merged Catalog (API + static fallback).
 *
 * Sections:
 *   1. Hero with eyebrow, headline, subtitle, dual CTAs and category image
 *   2. Trust strip — 4 quick reassurance pills
 *   3. Savings strip — country-by-country saving range
 *   4. Treatment card grid — every treatment in this category
 *   5. Why Vietnam for {category} — long-form reasons
 *   6. What to expect — concierge timeline
 *   7. FAQ accordion
 *   8. Related categories cross-link
 *   9. Final CTA
 */

const CategoryTreatmentsPage = () => {
  const { pathname } = useLocation();
  const { language } = useLanguage();
  const { getTreatmentsByCategory } = useCatalog();
  const { contactInfo } = useContactInfo();

  // Strip optional locale prefix and match against the urlSlug.
  const cleanPath = pathname.replace(/^\/[a-z]{2}(\/|$)/i, '/').replace(/\/$/, '') || '/';
  const content = getCategoryContentByUrl(cleanPath);

  if (!content) {
    // Unknown category — fall back to the treatments index.
    return <Navigate to={buildLocalizedPath('/treatments', language)} replace />;
  }

  const treatments = (getTreatmentsByCategory(content.categorySlug) || []).filter(Boolean);
  const whatsappHref = getWhatsAppHref(
    contactInfo,
    `Hi VietClinics! I'd like a quote for ${content.label.toLowerCase()} treatment in Vietnam.`,
  );

  // Related categories — exclude current.
  const related = ALL_CATEGORY_LANDINGS.filter((c) => c.categorySlug !== content.categorySlug);

  const canonical = typeof window !== 'undefined'
    ? `${window.location.origin}${content.urlSlug}`
    : `https://vietclinics.com${content.urlSlug}`;

  // JSON-LD — MedicalProcedure list scoped to this category.
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    name: content.metaTitle,
    description: content.metaDescription,
    url: canonical,
    inLanguage: 'en',
    ...(content.editorial?.lastUpdated && {
      dateModified: content.editorial.lastUpdated,
      lastReviewed: content.editorial.lastUpdated,
    }),
    ...(content.editorial?.reviewedBy && {
      reviewedBy: {
        '@type': 'Organization',
        name: content.editorial.reviewedBy,
      },
    }),
    about: {
      '@type': 'MedicalSpecialty',
      name: content.label,
    },
    hasPart: treatments.map((t) => ({
      '@type': 'MedicalProcedure',
      name: t.name,
      url: `${typeof window !== 'undefined' ? window.location.origin : 'https://vietclinics.com'}/treatments/${t.slug}`,
    })),
    mainEntity: (content.faq || []).map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://vietclinics.com';
  const breadcrumbJsonLd = buildBreadcrumbJsonLd(
    [
      { name: 'Home', url: '/' },
      { name: 'Treatments', url: '/treatments' },
      { name: content.label, url: content.urlSlug },
    ],
    origin,
  );

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{content.metaTitle}</title>
        <meta name="description" content={content.metaDescription} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={content.metaTitle} />
        <meta property="og:description" content={content.metaDescription} />
        <meta property="og:image" content={content.hero.image} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={content.hero.image} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <Header />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-white" data-testid={`category-hero-${content.categorySlug}`}>
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(180deg, #F0FAF8 0%, #FAFCFB 60%, #FFFFFF 100%)' }}
        />
        <div
          aria-hidden
          className="absolute top-[-10%] right-[-10%] rounded-full pointer-events-none"
          style={{
            width: 620, height: 620,
            background: 'radial-gradient(circle, rgba(31,167,161,0.18) 0%, transparent 70%)',
            filter: 'blur(80px)',
          }}
        />

        <div className="relative z-10 container mx-auto px-4 max-w-7xl pt-12 lg:pt-16 pb-16 lg:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-7 flex flex-col gap-6">
              <div
                className="inline-flex items-center gap-2 self-start px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.18em]"
                style={{ background: '#DCEFEC', color: '#1A8F8A' }}
              >
                <Sparkles className="h-3.5 w-3.5" />
                {content.hero.eyebrow}
              </div>

              <h1
                className="font-extrabold tracking-tight text-slate-900 leading-[1.05]"
                style={{ fontSize: 'clamp(2.3rem, 4.6vw, 3.6rem)', letterSpacing: '-0.02em' }}
              >
                {content.hero.title}
              </h1>

              <p className="text-slate-500 text-lg lg:text-xl leading-relaxed max-w-xl">
                {content.hero.subtitle}
              </p>

              <div className="flex flex-wrap gap-3 pt-2">
                <a
                  href="#treatments"
                  className="inline-flex items-center gap-2 text-white font-bold rounded-2xl px-7 py-4 transition-all duration-300 hover:-translate-y-0.5"
                  style={{
                    background: 'linear-gradient(135deg, #1FA7A1 0%, #178f8a 100%)',
                    boxShadow: '0 12px 24px rgba(31,167,161,0.25)',
                  }}
                  data-testid="category-hero-explore"
                >
                  <Stethoscope className="h-4 w-4" />
                  Explore {content.label.toLowerCase()} treatments
                </a>
                {whatsappHref && (
                  <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 bg-white text-[#1FA7A1] font-bold rounded-2xl px-7 py-4 border-2 border-[#1FA7A1] hover:bg-[#1FA7A1]/5 transition-all"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Free WhatsApp consult
                  </a>
                )}
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div
                className="relative rounded-[28px] overflow-hidden aspect-[4/5]"
                style={{ boxShadow: '0 30px 80px rgba(15,42,85,0.18)' }}
              >
                <img
                  src={content.hero.image}
                  alt={content.hero.title}
                  className="w-full h-full object-cover"
                  loading="eager"
                  onError={(e) => { e.target.src = content.hero.fallback; }}
                />
                <div
                  aria-hidden
                  className="absolute inset-0 pointer-events-none"
                  style={{ background: 'linear-gradient(135deg, rgba(31,167,161,0.08) 0%, transparent 35%, rgba(15,42,85,0.20) 100%)' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TRUST STRIP ──────────────────────────────────────────────────── */}
      <section className="bg-white border-y border-slate-100">
        <div className="container mx-auto px-4 max-w-7xl py-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: ShieldCheck, label: 'Verified partner clinics' },
              { icon: Award,       label: 'US/EU/Korean-trained doctors' },
              { icon: Plane,       label: 'Full concierge coordination' },
              { icon: TrendingDown, label: 'Up to 80% savings' },
            ].map(({ icon: Icon, label }, i) => (
              <div key={label} className="flex items-center gap-3" data-testid={`category-trust-${i + 1}`}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#DCEFEC' }}>
                  <Icon className="h-4 w-4 text-[#1A8F8A]" />
                </div>
                <p className="text-xs font-bold text-slate-800 leading-tight">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── INTRO (long-form SEO) ────────────────────────────────────────── */}
      {content.intro?.paragraphs?.length > 0 && (
        <section className="py-16 lg:py-20 bg-white" data-testid={`category-intro-${content.categorySlug}`}>
          <div className="container mx-auto px-4 max-w-4xl">
            {content.intro.heading && (
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-7">
                {content.intro.heading}
              </h2>
            )}
            <div className="space-y-5">
              {content.intro.paragraphs.map((p, idx) => (
                <p
                  key={idx}
                  className="text-slate-600 text-base lg:text-lg leading-relaxed"
                >
                  {p}
                </p>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── SAVINGS STRIP ────────────────────────────────────────────────── */}
      {content.savings?.length > 0 && (
        <section className="py-12 lg:py-16 bg-[#F8FAFC]">
          <div className="container mx-auto px-4 max-w-7xl">
            <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest text-center mb-3">
              Average savings
            </p>
            <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight text-center mb-8">
              How much you save vs your home country
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
              {content.savings.map((row) => (
                <div
                  key={row.fromCountry}
                  className="bg-white rounded-2xl border border-slate-100 p-5 text-center"
                  style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.05)' }}
                >
                  <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
                    From {row.fromCountry}
                  </p>
                  <p className="text-2xl lg:text-3xl font-extrabold text-[#1FA7A1] tracking-tight">
                    {row.save}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── COST COMPARISON TABLE ────────────────────────────────────────── */}
      {content.costTable?.rows?.length > 0 && (
        <section className="py-16 lg:py-20 bg-white" data-testid={`category-cost-${content.categorySlug}`}>
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="max-w-3xl mb-8">
              <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
                Cost comparison
              </p>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.1]">
                {content.costTable.heading}
              </h2>
            </div>
            <div
              className="bg-white rounded-2xl border border-slate-100 overflow-x-auto"
              style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.05)' }}
            >
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="bg-[#F0FAF8] border-b border-slate-100">
                    {content.costTable.headers.map((h) => (
                      <th
                        key={h}
                        className="px-4 lg:px-5 py-4 font-extrabold text-slate-900 text-xs uppercase tracking-wider whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {content.costTable.rows.map((r, idx) => (
                    <tr
                      key={idx}
                      className={`border-b border-slate-100 last:border-b-0 ${idx % 2 === 1 ? 'bg-slate-50/50' : ''}`}
                    >
                      <td className="px-4 lg:px-5 py-4 font-bold text-slate-900">{r.procedure}</td>
                      <td className="px-4 lg:px-5 py-4 font-extrabold text-[#1FA7A1] whitespace-nowrap">{r.vietnam}</td>
                      <td className="px-4 lg:px-5 py-4 text-slate-600 whitespace-nowrap">{r.usa}</td>
                      <td className="px-4 lg:px-5 py-4 text-slate-600 whitespace-nowrap">{r.uk}</td>
                      <td className="px-4 lg:px-5 py-4 text-slate-600 whitespace-nowrap">{r.australia}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {content.costTable.footnote && (
              <p className="text-xs text-slate-500 leading-relaxed mt-4 max-w-3xl">
                {content.costTable.footnote}
              </p>
            )}
          </div>
        </section>
      )}

      {/* ── TREATMENT CARDS ──────────────────────────────────────────────── */}
      <section id="treatments" className="py-20 bg-white" data-testid="category-treatments-grid">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="max-w-3xl mb-10">
            <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
              Treatments
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.08]">
              {content.label} treatments we coordinate in Vietnam
            </h2>
            <p className="text-slate-500 mt-4 text-base lg:text-lg leading-relaxed">
              Tap any treatment for procedure details, what to expect, recovery
              timeline and a transparent price estimate.
            </p>
          </div>

          {treatments.length === 0 ? (
            <div className="text-slate-500 text-center py-10">
              No treatments found in this category yet — check back soon.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {treatments.map((t) => (
                <Link
                  key={t.slug}
                  to={buildLocalizedPath(`/treatments/${t.slug}`, language)}
                  className="group bg-white rounded-[22px] border border-slate-100 overflow-hidden flex flex-col hover:-translate-y-1 transition-all duration-300"
                  style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.05)' }}
                  data-testid={`category-treatment-card-${t.slug}`}
                  aria-label={`${t.name} in Vietnam — pricing, recovery and guide`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
                    <img
                      src={t.image}
                      alt={t.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => { if (content.hero.fallback) e.target.src = content.hero.fallback; }}
                    />
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="font-extrabold text-slate-900 text-lg leading-snug mb-2">{t.name}</h3>
                    {t.shortDesc && (
                      <p className="text-sm text-slate-600 leading-relaxed flex-1 line-clamp-3">{t.shortDesc}</p>
                    )}
                    <div className="mt-4 inline-flex items-center justify-between">
                      {t.priceFrom && (
                        <span className="text-sm font-extrabold text-[#1FA7A1]">From {t.priceFrom}</span>
                      )}
                      <span className="text-sm font-bold text-[#1FA7A1] inline-flex items-center gap-1.5 group-hover:gap-2 transition-all ml-auto">
                        {t.name} in Vietnam <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── WHY VIETNAM ──────────────────────────────────────────────────── */}
      {content.whyPoints?.length > 0 && (
        <section className="py-20 bg-[#F8FAFC]">
          <div className="container mx-auto px-4 max-w-7xl">
            <div className="max-w-3xl mb-10">
              <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
                Why Vietnam
              </p>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.1]">
                Why international patients choose Vietnam for {content.label.toLowerCase()}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {content.whyPoints.map((point) => (
                <div
                  key={point.title}
                  className="bg-white rounded-2xl border border-slate-100 p-6"
                  style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.04)' }}
                >
                  <CheckCircle2 className="h-6 w-6 text-[#1FA7A1] mb-3" />
                  <h3 className="font-extrabold text-slate-900 text-base leading-snug mb-2">{point.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{point.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── TIMELINE ─────────────────────────────────────────────────────── */}
      {content.timeline?.length > 0 && (
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="text-center mb-10">
              <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
                How it works
              </p>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
                What to expect with VietClinics
              </h2>
            </div>
            <div className="space-y-4">
              {content.timeline.map((step, i) => (
                <div
                  key={step.title}
                  className="flex gap-5 bg-white rounded-2xl border border-slate-100 p-5 lg:p-6"
                  style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.04)' }}
                >
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center font-extrabold text-white flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, #1FA7A1 0%, #178f8a 100%)' }}
                  >
                    {i + 1}
                  </div>
                  <div>
                    <h3 className="font-extrabold text-slate-900 text-base mb-1">{step.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{step.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      {content.faq?.length > 0 && (
        <section className="py-20 bg-[#F8FAFC]">
          <div className="container mx-auto px-4 max-w-3xl">
            <div className="text-center mb-10">
              <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
                FAQ
              </p>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
                Frequently asked questions
              </h2>
            </div>
            <FAQAccordion items={content.faq} />
          </div>
        </section>
      )}

      {/* ── RELATED CATEGORIES ───────────────────────────────────────────── */}
      {related.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-7xl">
            <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight mb-6">
              Explore other specialties
            </h2>
            <div className="flex flex-wrap gap-2.5">
              {related.map((c) => (
                <Link
                  key={c.categorySlug}
                  to={buildLocalizedPath(c.urlSlug, language)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white text-slate-700 hover:text-[#1FA7A1] border border-slate-200 hover:border-[#1FA7A1] text-sm font-semibold transition-all"
                  data-testid={`category-related-${c.categorySlug}`}
                >
                  {c.label} in Vietnam
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── EDITORIAL / E-E-A-T BLOCK ────────────────────────────────────── */}
      {content.editorial && (
        <section className="py-12 bg-white border-t border-slate-100" data-testid={`category-editorial-${content.categorySlug}`}>
          <div className="container mx-auto px-4 max-w-4xl">
            <div
              className="bg-white rounded-2xl border border-slate-100 p-6 lg:p-8 flex flex-col sm:flex-row gap-5"
              style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.04)' }}
            >
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: '#DCEFEC' }}
              >
                <FileCheck2 className="h-5 w-5 text-[#1A8F8A]" />
              </div>
              <div className="flex-1">
                <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-1">
                  Reviewed by
                </p>
                <p className="font-extrabold text-slate-900 text-base mb-1">
                  {content.editorial.reviewedBy}
                  {content.editorial.role && (
                    <span className="text-slate-500 font-semibold"> — {content.editorial.role}</span>
                  )}
                </p>
                {content.editorial.methodology && (
                  <p className="text-sm text-slate-600 leading-relaxed mt-2">
                    {content.editorial.methodology}
                  </p>
                )}
                {content.editorial.lastUpdated && (
                  <p className="text-xs text-slate-500 mt-3">
                    Last reviewed: <time dateTime={content.editorial.lastUpdated}>{content.editorial.lastUpdated}</time>
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── FINAL CTA ────────────────────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <div
            className="rounded-[28px] p-10 lg:p-14 text-center text-white relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #0f2b55 0%, #1E5BA8 55%, #1FA7A1 100%)' }}
          >
            <div
              aria-hidden
              className="absolute inset-0 opacity-[0.10] pointer-events-none"
              style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, white 0%, transparent 50%)' }}
            />
            <div className="relative">
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-3">
                Ready to plan your {content.label.toLowerCase()} trip?
              </h2>
              <p className="text-white/85 text-lg max-w-xl mx-auto mb-7">
                Share your case — we&apos;ll send a USD quote + itinerary within 24 hours.
              </p>
              <Link
                to={buildLocalizedPath('/request-treatment', language)}
                className="inline-flex items-center gap-2 bg-white text-[#1FA7A1] font-bold rounded-full px-7 py-4 hover:-translate-y-0.5 transition-all"
              >
                Request a free quote
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

/* ── FAQ Accordion (lightweight) ─────────────────────────────────────────── */
const FAQAccordion = ({ items }) => {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <div className="space-y-3">
      {items.map((item, i) => {
        const open = openIdx === i;
        return (
          <div
            key={item.q}
            className="bg-white rounded-2xl border border-slate-100 overflow-hidden"
            style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.04)' }}
          >
            <button
              type="button"
              onClick={() => setOpenIdx(open ? -1 : i)}
              className="w-full flex items-center justify-between gap-4 p-5 text-left"
              aria-expanded={open}
            >
              <span className="font-extrabold text-slate-900 text-base leading-snug">{item.q}</span>
              <ChevronDown className={`h-5 w-5 text-[#1FA7A1] flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
              <div className="px-5 pb-5 text-slate-600 text-sm leading-relaxed">{item.a}</div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CategoryTreatmentsPage;
